/* eslint eqeqeq: "off" */
import { Fragment, memo, useState } from 'react';
import MUIDataTable from 'mui-datatables';
import CustomSearchBar from '../../components/CustomSearchBar';
import { Chip, IconButton, Tooltip } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import { centsToDollar, sortDollarColumn, aprToPercent, promosToChips, capitalizeString, humanizeSnakeCaseString} from '../../utils/formatters';
import { dealerNeedsReviewCell, isConciergeCell } from '../../utils/cellRenderers';
import { saveTableSession, getTableSession } from '../../utils/sessionHelpers';
import { customTableSearch } from '../../utils/tableHelpers.js';
import { FlagCell, LoanMetricTooltip, LoanTypeIcon, DealerMetricTooltip, CommentPreviewTooltip, IssuedReturnsCell } from '../../components';
import { useRole } from '../../contexts/RolesContext';
import red from '@material-ui/core/colors/red';
import { green, grey } from '@material-ui/core/colors';
import { LoanService } from '../../services';
import RepullButton from './RepullButton';
import ConfirmationModal from '../../components/ConfirmationModal'
import NotifyCheckboxCell from './NotifyCheckboxCell';
import PRCheckboxCell from './PRCheckboxCell.js';
import DeleteReturnsButton from './DeleteReturnsButton';
import BulkReturnTransitionButton, { RETURN_TRANSITIONS } from './BulkReturnTransitionButton';
import UpdateReturnsAmountCell from './UpdateReturnsAmountCell';
import { useError } from '../../contexts/ErrorContext';

  const tablifyReturns = returns =>
    returns == null ? null :
    returns.map(returnItem => {
      return [
        returnItem.id,
        returnItem.loan.loanNumber,
        returnItem.loan.dealer.dealerNumber,
        returnItem.loan.dealer.name,
        centsToDollar(returnItem.amountCents),
        centsToDollar(returnItem.feeAmountCents || 0),
        centsToDollar(returnItem.amountCents + (returnItem.feeAmountCents || 0)),
        (returnItem.loan.hasStagedFunding === true ? 'Yes' : 'No'),
        null,
        null,
        returnItem.status,
        returnItem.loan.dealer.organization,
        returnItem.loan.dealer.needsReview,
        returnItem.loan.dealer.isManualFlagged,
        returnItem.loan.lender.shortName,
        returnItem.loan.applicant.fullName,
        returnItem.loan.dealer?.isConcierge,
        centsToDollar(returnItem.loan.estimatedProjectAmountCents),
        centsToDollar(returnItem.loan.totalLoanAmountCents),
        returnItem.loan.termMonths,
        aprToPercent(returnItem.loan.apr),
        returnItem.loan.dealer?.manualFlaggedReason,
        returnItem.loan.dealer.dealerMetric?.totalFundedLoans || 0,
        promosToChips(returnItem.loan),
        returnItem.loan.projectType,
        returnItem.bounced,
        returnItem.notify,
        returnItem.commentCount,
        returnItem.loan.loanType,
        returnItem.isPrincipalReduction,
        returnItem.loan.dealer.dealerMetric.maxLoanAmount,
        returnItem.loan.dealer.dealerMetric.totalLoanAmount,
        returnItem.loan.dealer.dealerMetric.averageLoanAmount,
        returnItem.loan.dealer.dealerMetric.firstFundingDate,
        returnItem.loan.dealer.dealerMetric.lastFundingDate,
        returnItem.loan.dealer.dealerMetric.numberOfDelinquentLoans,
        returnItem.loan.dealer.dealerMetric.numberOfOverThirtyDayDelinquentLoans,
        returnItem.loan.comments,
        returnItem.alerts,
        returnItem.refundType,
        returnItem.loan.fundingRequest.applicationDate,
        returnItem.returnDate,
        returnItem,
        returnItem.postReturnAction,
        returnItem.funded ? 'Funded' : 'Non Funded',
      ];
    });

  const ReturnsTable = ({ returns, selectedDate, toggleShowNewReturn }) => {
      const [selectedRowIndices, setSelectedRowsIndices] = useState([]);
      const [showConfirmation, setShowConfirmation] = useState(false);
      const { isDailyProcessingWrite, isAdmin } = useRole();
      const starterColumns = ['Loan ID', 'Dealer #', 'Dealer', 'Amount', 'Fee Amount', 'Total Amount', 'Refund Type', 'Staged Funding', 'Completed Payments', 'Status', 'Org', 'Dealer Account Flag', 'Manual Flag', 'Lender', 'Notify?', 'Comments', 'Loan Type', 'Principal Reduction', 'Promos', 'Alerts', 'Post Return Action', 'Funded'];
      const [visibleColumns, setVisibleColumns] = useState((sessionStorage.getItem("returnsTable")) ? getTableSession("returnsTable") : saveTableSession(starterColumns, "returnsTable"));
      const [matchExactText, setmatchExactText] = useState(true);
      const [rowsPerPage, setRowsPerPage] = useState(50);
      const { showError } = useError();

      const handleMatchExactText = (event) => {
        setmatchExactText(event.target.checked);
      }

      const handleAddReturnClick = () => {
        toggleShowNewReturn();
      }

      const handlePullClick = () => {
        LoanService.pullReturns(selectedDate).then(response => {
          // !FIXME: Do this better!
          setTimeout(() => window.location.reload(), 1000);
        }).catch(showError);
        toggleConfirmation();
      };

      const toggleConfirmation = () => {
        setShowConfirmation(!showConfirmation);
      }

      const columns = [
        {
          name: 'Return ID',
          options: {
            filter: false,
            display: false,
            viewColumns: false,
          }
        },
        {
          name: 'Loan ID',
          options: {
              filter: false,
              display: visibleColumns ? visibleColumns.includes('Loan ID') : true,
              customBodyRender: (value, tableMeta) => {
                const item = tableMeta.rowData[42];
                return <LoanMetricTooltip
                          loanId={item.loan.loanNumber}
                          estimatedProjectAmount={centsToDollar(item.loan.estimatedProjectAmountCents)}
                          totalLoanAmount={centsToDollar(item.loan.totalLoanAmountCents)}
                          loanTerm={item.loan.termMonths}
                          loanApr={item.loan.apr}
                          promos={promosToChips(item.loan)}
                          projectType={item.loan.projectType}
                          applicationDate={item.loan.fundingRequest?.applicationDate}
                          returnDate={item.returnDate}
                        />
              }
          }
        },
        {
          name: 'Dealer #',
          options: {
            filter: false,
            display: visibleColumns ? visibleColumns.includes('Dealer #') : true,
            customBodyRender: value => <a href={`/dealers/${value}`}>{value}</a>
          }
        },
        {
          name: 'Dealer',
          options: {
            display: visibleColumns ? visibleColumns.includes('Dealer') : true,
            customBodyRender: (value, tableMeta) => {
              const item = tableMeta.rowData[42];
              return <DealerMetricTooltip
                        dealerName={value}
                        totalFundedLoans={item.loan.dealer.dealerMetric.totalFundedLoans || 0}
                        maxLoanAmount={centsToDollar(item.loan.dealer.dealerMetric.maxLoanAmount)}
                        totalLoanAmount={centsToDollar(item.loan.dealer.dealerMetric.totalLoanAmount)}
                        averageLoanAmount={centsToDollar(item.loan.dealer.dealerMetric.averageLoanAmount)}
                        firstFundingDate={item.loan.dealer.dealerMetric.firstFundingDate}
                        lastFundingDate={item.loan.dealer.dealerMetric.lastFundingDate}
                        dealerConcierge={item.loan.dealer.isConcierge ? "Yes" : "No"}
                        dealerDelinquentLoans={item.loan.dealer.dealerMetric.delinquentLoansCount == null ? 0 : item.loan.dealer.dealerMetric.delinquentLoansCount}
                        dealerThirtyDayDelinquentLoansCount={item.loan.dealer.dealerMetric.delinquentLoansOverThirtyDaysCount == null ? 0 : item.loan.dealer.dealerMetric.delinquentLoansOverThirtyDaysCount }
                        dealerAllTimeAverageCreditScore={item.loan.dealer.dealerMetric.allTimeAverageCreditScorePb == null ? 0 : item.loan.dealer.dealerMetric.allTimeAverageCreditScorePb}
                        dealerLastYearAverageCreditScore={item.loan.dealer.dealerMetric.lastYearAverageCreditScorePb == null ? 0 : item.loan.dealer.dealerMetric.lastYearAverageCreditScorePb}
                      />
            }
          }
        },
        {
          name: 'Amount',
          options: {
              filter: false,
              display: visibleColumns ? visibleColumns.includes('Amount') : true,
              customBodyRender: (value, tableMeta) => {
                const item = tableMeta.rowData[42];
                return <UpdateReturnsAmountCell
                          isDPWrite={isDailyProcessingWrite()}
                          amount={value}
                          bounced={item.bounced}
                          tableMeta={tableMeta}
                          returnDate={item.returnDate}
                        />
              },
              sortCompare: sortDollarColumn
          }
        },
        {
          name: 'Fee Amount',
          options: {
            filter: false,
            display: visibleColumns ? visibleColumns.includes('Fee Amount') : true,
            sortCompare: sortDollarColumn
          }
        },
        {
          name: 'Total Amount',
          options: {
            filter: false,
            display: visibleColumns ? visibleColumns.includes('Total Amount') : true,
            sortCompare: sortDollarColumn
          }
        },
        {
          name: 'Staged Funding',
          options: {
            display: visibleColumns ? visibleColumns.includes('Staged Funding') : true,
            customFilterListOptions: {
              render: v => v === "No" ? "Non-Staged Funded" : "Staged Funded",
            },
          }
        },
        {
          name: 'Stage',
          options: {
            filter: false,
            display: false,
            viewColumns: false
          }
        },
        {
          name: 'Completed Payments',
          options: {
              filter: false,
              display: visibleColumns ? visibleColumns.includes('Completed Payments') : true,
              customBodyRender: (value, tableMeta) => {
                const item = tableMeta.rowData[42];
                return <IssuedReturnsCell
                  id={item.id}
                  hasMultiplePartialRefunds={item.hasMultiplePartialRefunds}
                  alreadyPaidStages={item.loan.stagesAlreadyPaid}
                  heldStages={item.heldStages}
                  numberOfPaymentStages={item.loan.numberOfPaymentStages}
                  hasStagedFunding={item.loan.hasStagedFunding}
                  multipleRefundsIncludingFullRefund={item.multipleRefundsIncludingFullRefund}
                />
              }
          }
        },
        {
          name: 'Status',
          options: {
            display: visibleColumns ? visibleColumns.includes('Status') : true,
            customBodyRender: capitalizeString
          }
        },
        {
          name: 'Org',
          options: {
            display: visibleColumns ? visibleColumns.includes('Org') : true,
          }
        },
        {
          name: 'Dealer Account Flag',
          options: {
            display: visibleColumns ? visibleColumns.includes('Dealer Account Flag') : true,
            customBodyRender: dealerNeedsReviewCell,
            customFilterListOptions: {
              render: v => v ? "Dealer Flagged" : "Dealer Not Flagged",
            },
          }
        },
        {
          name: "Manual Flag",
          options: {
            display: visibleColumns ? visibleColumns.includes('Manual Flag') : true,
            customBodyRender: (value, tableMeta) => {
              const item = tableMeta.rowData[42];
              return ( isDailyProcessingWrite() ?
                <FlagCell value={value} dealerNumber={item.loan.dealer.dealerNumber} flagReason={item.loan.dealer.manualFlaggedReason} /> :
                <FlagCell value={value} dealerNumber={item.loan.dealer.dealerNumber} flagReason={item.loan.dealer.manualFlaggedReason} disableUpdates={true} />
              );
            },
            customFilterListOptions: {
              render: v => v ? "Manual Flagged" : "Non-Manual Flagged",
            },
          }
        },
        {
          name: 'Lender',
          options: {
            display: visibleColumns ? visibleColumns.includes('Lender') : true,
          }
        },
        {
          name: 'Borrower',
          options: {
            display: visibleColumns ? visibleColumns.includes('Borrower') : true,

          }
        },
        {
          name: 'Concierge Dealer',
          options: {
            filter: false,
            display: visibleColumns ? visibleColumns.includes('Concierge Dealer') : true,
            customBodyRender: isConciergeCell,
          }
        },
        {
          name: 'estimatedProjectAmount',
          options: {
            filter: false,
            display: false,
            viewColumns: false,
          }
        },
        {
          // This comes from loan table not FR table
          name: 'hiddenTotalLoanAmount',
          options: {
            filter: false,
            display: false,
            viewColumns: false,
          }
        },
        {
          name: 'term',
          options: {
            filter: false,
            display: false,
            viewColumns: false,
          }
        },
        {
          name: 'apr',
          options: {
            filter: false,
            display: false,
            viewColumns: false,
          }
        },
        {
          name: 'Manual Flag Reason',
          options: {
              filter: false,
              display: false,
              viewColumns: false
          }
        },
        {
          name: 'Total Funded Loans',
          options: {
              filter: false,
              display: visibleColumns ? visibleColumns.includes('Total Funded Loans') : true,
          }
        },
        {
          name: 'Promos',
          options: {
              filter: false,
              display: visibleColumns ? visibleColumns.includes('Promos') : true,
          }
        },
        {
          name: 'Project Type',
          options: {
              filter: false,
              display: visibleColumns ? visibleColumns.includes('Project Type') : true,
          }
        },
        {
          name: 'Bounced',
          options: {
              filter: true,
              display: false,
              viewColumns: false,
              customFilterListOptions: {
                render: v => v ? "Bounced Payments" : "Non Bounced Payments",
              }
          }
        },
        {
          name: 'Notify?',
          options: {
              filter: false,
              display: visibleColumns ? visibleColumns.includes('Notify?') : true,
              customBodyRender: (value, tableMeta) => {
                const item = tableMeta.rowData[42];
                return (
                  (isDailyProcessingWrite() || isAdmin()) ?
                    <NotifyCheckboxCell value={value} id={item.id} transactionType={'return'} date={selectedDate} disableUpdates={false} loanNumber={tableMeta.rowData[1]} /> :
                    <NotifyCheckboxCell value={value} id={item.id} transactionType={'return'} date={selectedDate} disableUpdates={true} loanNumber={tableMeta.rowData[1]} />
                  )
            }
          }
        },
        {
          name: 'Comments',
          options: {
              filter: false,
              display: visibleColumns ? visibleColumns.includes('Comments') : true,
              /*ignore jslint start*/
              customBodyRender: (value, tableMeta) => {
                let comments = tableMeta.rowData[37];
                return (comments.length === 0) ? null : (<CommentPreviewTooltip count={comments.length} comments={comments} />)
              }
              /*ignore jslint end*/
          }
        },
        {
          name: 'Loan Type',
          options: {
              filter: true,
              display: visibleColumns ? visibleColumns.includes('Loan Type') : true,
              sort: false,
              viewColumns: true,
              filterOptions: {
                names: ['Home Improvement', 'Healthcare', 'Solar'],
              },
              customBodyRender: value => <LoanTypeIcon loanType={value} />
          }
        },
        {
          name: 'Principal Reduction',
          options: {
              filter: true,
              display: visibleColumns ? visibleColumns.includes('Principal Reduction') : true,
              customBodyRender: (value, tableMeta) => {
                const item = tableMeta.rowData[42];
                return (
                  (isDailyProcessingWrite() || isAdmin()) ?
                  <PRCheckboxCell value={item.isPrincipalReduction} id={item.id} disableUpdates={false} date={selectedDate}/> :
                  <PRCheckboxCell value={item.isPrincipalReduction} id={item.id} disableUpdates={true} date={selectedDate}/>
                )
              },
              customFilterListOptions: {
                render: v => v ? "Principal Reduction" : "Non Principal Reduction",
              }
          }
        },
        {
          name: 'Dealer Max Loan Amount',
          options: {
            filter: false,
            display: false,
            viewColumns: false,
          }
        },
        {
          name: 'Dealer Total Loan Amount',
          options: {
            filter: false,
            display: false,
            viewColumns: false,
          }
        },
        {
          name: 'Dealer Average Loan Amount',
          options: {
            filter: false,
            display: false,
            viewColumns: false,
          }
        },
        {
          name: 'Dealer First Funding Date',
          options: {
            filter: false,
            display: false,
            viewColumns: false,
          }
        },
        {
          name: 'Dealer Latest Funding Date',
          options: {
            filter: false,
            display: false,
            viewColumns: false,
          }
        },
        {
          name: 'Dealer Delinquent Loans',
          options: {
            filter: false,
            display: false,
            viewColumns: false,
          }
        },
        {
          name: 'Dealer 30+ Delinquent Loans',
          options: {
            filter: false,
            display: false,
            viewColumns: false,
          }
        },
        {
          name: 'Comment Messages',
          options: {
            filter: false,
            display: false,
            viewColumns: false,
          }
        },
        {
          name: 'Alerts',
          options: {
            filter: false,
            display: visibleColumns ? visibleColumns.includes('Alerts') : true,
            viewColumns: true,
            customBodyRender: value => {
              let msgs = [];
              if (value.noPaymentsCompleted) {
                msgs.push('No completed payments for this return.');
              }
                return msgs.length > 0
                  ? msgs.map((msg, idx) => <div key={idx}>{msg}</div>)
                  : 'N/A';
            }
          }
        },
        {
          name: 'Refund Type',
          options: {
            filter: false,
            display: visibleColumns ? visibleColumns.includes('Refund Type') : true,
            customBodyRender: (value, tableMeta) => tableMeta.rowData[42].refundType
          }
        },
        {
          name: 'Application Date',
          options: {
            filter: false,
            display: false,
            viewColumns: false,
          }
        },
        {
          name: 'Return Date',
          options: {
            filter: false,
            display: false,
            viewColumns: false,
          }
        },
        // allows us to access whole return object vs shifting indices to access data in customBodyRender
        {
          name: 'item',
          options: {
            display: false,
            filter: false,
            viewColumns: false
          }
        },
        {
          name: 'Post Return Action',
          options: {
            display: true,
            filter: true,
            viewColumns: true,
            customBodyRender: (value, tableMeta) => humanizeSnakeCaseString(tableMeta.rowData[42].postReturnAction)
          }
        },
        {
          // Non funded returns are left out of the returns NACHA
          name: 'Funded',
          options: {
            filter: true,
            display: visibleColumns ? visibleColumns.includes('Funded') : true,
            customBodyRender: value => (
              <Chip
                size="small"
                label={value}
                style={{ backgroundColor: value === 'Funded' ? green[600] : grey[500], color: 'white' }}
              />
            )
          }
        }
      ];

      const options = {
        filterType: 'dropdown',
        print: false,
        download: isDailyProcessingWrite(),
        onDownload: () => {
          LoanService.downloadReturnsReport(selectedDate).catch(showError);
          return false;
        },
        searchOpen: true,
        rowsPerPage: rowsPerPage,
        rowsPerPageOptions: [50, 100, 250],
        rowsSelected: selectedRowIndices,
        setRowProps: (row, dataIndex, rowIndex) => {
          if (row[42].bounced) {
            return {
              style: {background: red[200]}
            };
          }
          else if (rowIndex % 2 === 0) {
            return {
              style: { background: 'snow' }
            };
          }
        },
        onRowSelectionChange: (rowsSelected, allRows) => {
          setSelectedRowsIndices(allRows.map(row => row.dataIndex));
        },
        onTableChange: (action, tableState) => {
          switch (action) {
            case 'viewColumnsChange':
              let displayedVisibleColumns = tableState.columns.map(column => {
                if (column.display === "true") {
                  return column.name;
                } else {
                  return null;
                }
              }).filter(column => column !== null);

              if (sessionStorage.getItem("returnsTable")) {
                saveTableSession(displayedVisibleColumns, "returnsTable");
              }
              setVisibleColumns(displayedVisibleColumns);

              break;
            default:
              break;
          }
        },
        selectToolbarPlacement: 'none',
        customToolbar: () => {
          if (selectedRowIndices.length > 0) {
            return (isDailyProcessingWrite() || isAdmin()) ? (
              <Fragment>
                <BulkReturnTransitionButton selectedRowIndices={selectedRowIndices} returns={returns} transition={RETURN_TRANSITIONS.PROCESS} />
                <BulkReturnTransitionButton selectedRowIndices={selectedRowIndices} returns={returns} transition={RETURN_TRANSITIONS.BOUNCE} />
                <DeleteReturnsButton selectedRowIndices={selectedRowIndices} potentialDeletions={returns} date={selectedDate}  />
              </Fragment>
            ) : null;
        } else {
          const selectedDateIsToday = new Date().toDateString() === new Date(selectedDate).toDateString();

          return isDailyProcessingWrite() && selectedDateIsToday ? (
                  <Fragment>
                    <Tooltip title={'Add New Return'}>
                      <IconButton onClick={handleAddReturnClick}>
                        <AddIcon />
                      </IconButton>
                    </Tooltip>
                    <RepullButton
                        hasItems={returns.length > 0}
                        pullName={'Returns'}
                        onClick={toggleConfirmation}
                        selectedDate={selectedDate}
                    />
                  </Fragment>
                ) : null;
        }
        },
        customSearchRender: (searchText, handleSearch, hideSearch, options) => {
          return (
            <CustomSearchBar
              searchText={searchText}
              handleSearch={handleSearch}
              hideSearch={hideSearch}
              options={options}
              matchExactText={matchExactText}
              handleMatchExactText={handleMatchExactText}
            />
          );
        },
        customSearch: (searchQuery, currentRow, columns) => {
          return customTableSearch(searchQuery, currentRow, columns, matchExactText);
        },
        onChangeRowsPerPage: (rowNumber) => {
          setRowsPerPage(rowNumber);
        },
      };

      return returns === null ? (
        <h1>Loading...</h1>
      ) : (
        <Fragment>
          <ConfirmationModal
              title="Confirm"
              message={`Are you sure you want to pull returns for ${new Date(selectedDate).toLocaleDateString()}?`}
              showConfirmationBox={showConfirmation}
              onConfirm={handlePullClick}
              onCancel={toggleConfirmation}
          />
          <MUIDataTable data={tablifyReturns(returns)} columns={columns} options={options} />
        </Fragment>
      );
  }

  export default memo(ReturnsTable);
